<script setup>
import { useSurgeonStore } from '@/stores/surgeon'
import { computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'

// Import components
import HospitalsList from '@/views/pages/surgeon/HospitalsList.vue'
import ProfileDetails from '@/views/pages/surgeon/ProfileDetails.vue'

// import ScheduleCalendar from '@/views/pages/surgeon/ScheduleCalendar.vue' // Temporarily disabled
import SpecialtiesList from '@/views/pages/surgeon/SpecialtiesList.vue'

// Store
const surgeonStore = useSurgeonStore()

// Routing
const route = useRoute('surgeon-id-tab')
const surgeonId = computed(() => route.params.id)
const isCreateMode = computed(() => surgeonId.value === 'new')

const activeTab = computed({
  get: () => route.params.tab || 'profile',
  set: () => route.params.tab,
})

// Fetch surgeon data
onMounted(() => {
  if (isCreateMode.value)
    surgeonStore.startNewSurgeon()
  else if (surgeonStore.surgeon?.id !== surgeonId.value)
    surgeonStore.fetchSurgeon(surgeonId.value)
})

watch(surgeonId, value => {
  if (value === 'new')
    surgeonStore.startNewSurgeon()
  else if (surgeonStore.surgeon?.id !== value)
    surgeonStore.fetchSurgeon(value)
})

onUnmounted(() => {
  surgeonStore.reset()
})

// Tab configuration
const tabs = [
  {
    title: 'Profile Details',
    icon: 'ri-user-line',
    tab: 'profile',
    component: 'ProfileDetails',
  },
  {
    title: 'Hospitals',
    icon: 'ri-hospital-line',
    tab: 'hospitals',
    component: 'HospitalsList',
  },
  {
    title: 'Specialties',
    icon: 'ri-stethoscope-line',
    tab: 'specialties',
    component: 'SpecialtiesList',
  },

  // {
  //   title: 'Schedule',
  //   icon: 'ri-calendar-line',
  //   tab: 'schedule',
  //   component: 'ScheduleCalendar',
  // },
]

const componentMap = {
  ProfileDetails,
  HospitalsList,
  SpecialtiesList,

  // ScheduleCalendar,
}

definePage({
  meta: {
    layout: 'default',
    requiresAuth: true,
  },
})
</script>

<template>
  <div>
    <!-- Loading -->
    <div
      v-if="surgeonStore.loading && !surgeonStore.surgeon"
      class="text-center py-8"
    >
      <VProgressCircular indeterminate />
    </div>

    <!-- Content -->
    <div v-else-if="surgeonStore.surgeon">
      <VAlert
        v-if="surgeonStore.error"
        type="error"
        class="mb-6"
      >
        {{ surgeonStore.error }}
      </VAlert>

      <!-- Header -->
      <VCard class="mb-6">
        <VCardText>
          <div class="d-flex align-center gap-4">
            <VAvatar size="64">
              <span class="text-h5">
                {{ surgeonStore.surgeon.first_name?.charAt(0) || 'N' }}
              </span>
            </VAvatar>
            <div>
              <h2 class="text-h5 font-weight-bold">
                {{ isCreateMode ? 'New Surgeon' : surgeonStore.fullName }}
              </h2>
              <p class="text-body-2 text-medium-emphasis">
                GMC: {{ surgeonStore.surgeon.gmc_number || '—' }}
              </p>
              <VChip
                v-if="!isCreateMode"
                size="small"
                :color="surgeonStore.isActive ? 'success' : 'error'"
              >
                {{ surgeonStore.isActive ? 'Active' : 'Inactive' }}
              </VChip>
            </div>
          </div>
        </VCardText>
      </VCard>

      <!-- Tabs Navigation -->
      <VTabs v-model="activeTab">
        <VTab
          
          v-for="item in tabs"
          :key="item.tab"
          :value="item.tab"
          :to="{
            name: 'surgeon-id-tab',
            params: {
              id: surgeonId,
              tab: item.tab 
            }
          }"
          :disabled="isCreateMode && item.tab !== 'profile'"
        >
          <VIcon
            start
            :icon="item.icon"
          />
          {{ item.title }}
        </VTab>
      </VTabs>

      <!-- Tabs Content -->
      <VWindow
        v-model="activeTab"
        class="disable-tab-transition"
        :touch="false"
      >
        <VWindowItem
          v-for="item in tabs"
          :key="item.tab"
          :value="item.tab"
        >
          <component :is="componentMap[item.component]" />
        </VWindowItem>
      </VWindow>
    </div>
  </div>
</template>
